import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";

import { FaTimes, FaClipboardList, FaUserClock, FaUser, FaCheck } from "react-icons/fa";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import { border } from "@mui/system";
import { useEffect, useState } from "react";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import useCommon from "hooks/useCommon";
import { getRequestsByStay } from "store/slice/requestSlice";
import { resetStayRequestsState } from "store/slice/requestSlice";
import dayjs from "utils/dayjs";
import CardSkeleton from "components/Skeletons/CardSkeleton";
import NoResults from "components/NoResult";
import { resetUpdateRequestState } from "store/slice/requestSlice";
import { updateRequest } from "store/slice/requestSlice";
import { CircularProgress } from "@mui/material";

const STATUS_OPTIONS = ["pending", "in_progress", "completed"];

const PRIORITY_OPTIONS = ["low", "medium", "high"];

const STATUS_STYLE = {
  pending: {
    label: "Pending",
    color: "#f59e0b",
    border: "rgba(245,158,11,0.45)",
  },
  in_progress: {
    label: "In Progress",
    color: "#3b82f6",
    border: "rgba(59,130,246,0.45)",
  },
  completed: {
    label: "Completed",
    color: "#22c55e",
    border: "rgba(34,197,94,0.45)",
  },
};

const PRIORITY_CONFIG = {
  low: {
    label: "LOW PRIORITY",
    color: "#8A8A8A",
    border: "rgba(138,138,138,0.45)",
  },
  medium: {
    label: "MEDIUM PRIORITY",
    color: "#F5C542",
    border: "rgba(245,197,66,0.55)",
  },
  high: {
    label: "HIGH PRIORITY",
    color: "#FF5C5C",
    border: "rgba(255,92,92,0.6)",
  },
};

const { black, dark, primary, success, light } = colors;
const { borderWidth } = borders;

const ConciergeRequestsModal = ({ open, guest, onClose, canEdit = false }) => {
  const { STORE, DISPATCH } = useCommon();

  const { stayRequests, stayRequestsLoading, stayRequestsError } = STORE((state) => state.requests);

  console.log("Stay Requests from store:", stayRequests);

  const [editableRequests, setEditableRequests] = useState(stayRequests);
  const [dirtyMap, setDirtyMap] = useState({});
  const [savingMap, setSavingMap] = useState({});

  const markDirty = (id) => {
    setDirtyMap((prev) => ({ ...prev, [id]: true }));
  };

  const handleRequestChange = (id, field, value) => {
    setEditableRequests((prev) =>
      prev.map((req) => (req.id === id ? { ...req, [field]: value } : req))
    );

    setDirtyMap((prev) => ({ ...prev, [id]: true }));
  };

  const handleUpdateRequest = async (request) => {
    try {
      setSavingMap((prev) => ({ ...prev, [request.id]: true }));

      await DISPATCH(
        updateRequest({
          requestId: request.requestId || request.id,
          payload: {
            reservationId: request.reservationId || guest?.reservationId,
            title: request.title,
            description: request.description,
            status: request.status,
          },
        })
      ).unwrap();

      DISPATCH(resetUpdateRequestState());

      setDirtyMap((prev) => {
        const copy = { ...prev };
        delete copy[request.id];
        return copy;
      });
    } catch (err) {
      console.error("Failed to update request", err);
    } finally {
      setSavingMap((prev) => ({ ...prev, [request.id]: false }));
    }
  };

  useEffect(() => {
    if (!open || !guest?.id || !guest?.reservationId) return;

    DISPATCH(
      getRequestsByStay({
        guestId: guest.id,
        reservationId: guest.reservationId,
        status: "all",
        page: 1,
        pageSize: 200,
        sortBy: "due",
        sortDir: "asc",
      })
    );

    return () => {
      DISPATCH(resetStayRequestsState());
    };
  }, [open, guest?.id, guest?.reservationId]);

  useEffect(() => {
    if (stayRequests?.length) {
      const formatted = stayRequests.map((r) => {
        return {
          id: r._id,
          requestId: r._id,
          reservationId: r.reservationId,
          title: r.title || "",
          description: r.description || "",
          status: r.status || "pending",
          priority: "medium",

          createdAt: r.createdAt,
          dueDate: r.dueDate || null,
          dueTime: r.dueTime || null,

          assignedToUsers: r.assignedToUsers || [],
        };
      });

      setEditableRequests(formatted);
    } else {
      setEditableRequests([]);
    }
  }, [stayRequests]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* ================= HEADER ================= */}
      <DialogTitle>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between">
          <VuiBox display="flex" alignItems="center" gap={1}>
            <FaClipboardList size={16} color={primary.main} />
            <VuiTypography variant="lg">Guest Requests</VuiTypography>
          </VuiBox>

          <IconButton size="small" onClick={onClose}>
            <FaTimes color={primary.main} />
          </IconButton>
        </VuiBox>

        {guest && (
          <VuiTypography variant="caption" mt={0.5}>
            {guest.name} · {guest.room}
          </VuiTypography>
        )}
      </DialogTitle>

      {/* ================= CONTENT ================= */}
      <DialogContent
        sx={{
          pt: 2,
        }}
      >
        {stayRequestsLoading ? (
          <VuiBox display="grid" gap={2}>
            {Array.from({ length: 3 }).map((_, i) => (
              <CardSkeleton key={i} lines={4} hasHeader hasFooter />
            ))}
          </VuiBox>
        ) : stayRequestsError ? (
          <VuiBox
            p={2}
            borderRadius="lg"
            sx={{
              border: "1px solid rgba(255,82,82,0.35)",
              background: "rgba(255,82,82,0.06)",
            }}
          >
            <VuiTypography variant="caption" color="error">
              {stayRequestsError}
            </VuiTypography>
          </VuiBox>
        ) : editableRequests.length === 0 ? (
          <NoResults title="No requests found" />
        ) : (
          <VuiBox display="grid" gap={2}>
            {editableRequests.map((r) => {
              const priority = PRIORITY_CONFIG[r.priority];
              const statusStyle = STATUS_STYLE[r.status];

              return (
                <VuiBox
                  key={r.id}
                  p={2}
                  borderRadius="16px"
                  sx={{
                    position: "relative",
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
                    border: `1px solid ${statusStyle.border}`,
                  }}
                >
                  {/* Title + Status */}
                  <VuiBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                    mb={1}
                  >
                    <VuiBox flex={1}>
                      <VuiTypography variant="button">{r.title}</VuiTypography>

                      <VuiTypography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5, opacity: 0.75 }}
                      >
                        Created ·{" "}
                        {r.createdAt ? dayjs.tz(r.createdAt).format("MMM D • h:mm A") : "-"}
                      </VuiTypography>
                    </VuiBox>

                    {canEdit ? (
                      <select
                        value={r.status}
                        onChange={(e) => handleRequestChange(r.id, "status", e.target.value)}
                        style={{
                          background: "transparent",
                          border: `1px solid ${statusStyle.border}`,
                          borderRadius: "4px",
                          padding: "2px 6px",
                          color: statusStyle.color,
                          fontSize: "9px",
                          letterSpacing: "0.1em",
                          fontWeight: 600,
                          outline: "none",
                          cursor: "pointer",
                        }}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option
                            key={opt}
                            value={opt}
                            style={{
                              color: "#000",
                              background: "#fff",
                              fontWeight: 500,
                            }}
                          >
                            {STATUS_STYLE[opt].label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <VuiTypography
                        variant="caption"
                        sx={{
                          borderRadius: "4px",
                          px: "6px",
                          py: "2px",
                          fontSize: "9px",
                          letterSpacing: "0.1em",
                          fontWeight: 600,
                          lineHeight: 1,
                          color: statusStyle.color,
                          border: `1px solid ${statusStyle.border}`,
                          opacity: 0.9,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {statusStyle.label}
                      </VuiTypography>
                    )}
                  </VuiBox>

                  {/* Description */}
                  {canEdit ? (
                    <VuiInput
                      gradient
                      multiline
                      value={r.description}
                      onChange={(e) => handleRequestChange(r.id, "description", e.target.value)}
                      rows={2}
                    />
                  ) : (
                    r.description && (
                      <VuiTypography variant="body2" sx={{ opacity: 0.9 }} mb={1}>
                        {r.description}
                      </VuiTypography>
                    )
                  )}

                  {/* Save Changes */}
                  {canEdit && dirtyMap[r.id] && (
                    <VuiBox display="flex" justifyContent="center" mt={1.5}>
                      <VuiButton
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ px: 2, minWidth: "140px" }}
                        onClick={() => handleUpdateRequest(r)}
                        disabled={savingMap[r.id]}
                        startIcon={
                          savingMap[r.id] ? (
                            <CircularProgress size={14} thickness={5} sx={{ color: "#22c55e" }} />
                          ) : (
                            <FaCheck size={10} />
                          )
                        }
                      >
                        {savingMap[r.id] ? "Saving..." : "Save changes"}
                      </VuiButton>
                    </VuiBox>
                  )}

                  {/* Footer */}
                  <VuiBox
                    mt={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                  >
                    {r.assignedToUsers?.length > 0 && (
                      <VuiBox display="flex" alignItems="center" gap={0.6}>
                        <VuiBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width={18}
                          height={18}
                          borderRadius="50%"
                          sx={{
                            background: "rgba(255,255,255,0.15)",
                            border: "1px solid rgba(255,255,255,0.25)",
                          }}
                        >
                          <FaUser size={8} color="#fff" />
                        </VuiBox>

                        <VuiTypography variant="caption">
                          Assigned to{" "}
                          {r.assignedToUsers?.length
                            ? r.assignedToUsers.map((u) => u.name).join(", ")
                            : "Unassigned"}
                        </VuiTypography>
                      </VuiBox>
                    )}

                    {r.dueDate && (
                      <VuiBox display="flex" alignItems="center" gap={0.5}>
                        <FaUserClock size={12} color="rgba(255,255,255,0.6)" />

                        <VuiTypography variant="caption">
                          Due ·{" "}
                          {dayjs
                            .tz(r.dueTime ? `${r.dueDate.slice(0, 10)}T${r.dueTime}` : r.dueDate)
                            .format(r.dueTime ? "MMM D • h:mm A" : "MMM D")}
                        </VuiTypography>
                      </VuiBox>
                    )}
                  </VuiBox>
                </VuiBox>
              );
            })}
          </VuiBox>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConciergeRequestsModal;
